import { subscriptionApiService } from './subscriptionApi';
import { paymentApiService } from './paymentApi';
import type {
  Subscription,
  Invoice,
  PaymentTransaction,
  CreateSubscriptionRequest,
  ProcessPaymentRequest,
  ChangePlanRequest,
  CancelSubscriptionRequest,
  CouponValidationResult,
  AvailablePaymentMethod,
  SubscriptionStatusResponse,
} from '@/types/billing';
import type { ApiResponse, PaginatedResponse } from '@/types/subscription';

// Subscription Service API Base URL (used for raw fetch of PDF blobs)
const SUBSCRIPTION_API_BASE_URL =
  import.meta.env.VITE_SUBSCRIPTION_API_BASE_URL || '/api/subscription';

// Helper to unwrap ApiResponse (same pattern as subscriptionApi.ts)
const unwrapResponse = <T>(response: ApiResponse<T> | T): T => {
  // If response is already the data type (no wrapper)
  if (response && typeof response === 'object' && !('success' in response)) {
    return response as T;
  }
  // If response is wrapped in ApiResponse
  const wrapped = response as ApiResponse<T>;
  if (!wrapped.success || !wrapped.data) {
    throw new Error(wrapped.message || 'API request failed');
  }
  return wrapped.data;
};

// ============================================================================
// Billing API
// ============================================================================

export const billingApi = {
  // --------------------------------------------------------------------------
  // Subscriptions
  // --------------------------------------------------------------------------

  /** Create a new subscription (checkout flow). */
  createSubscription: async (data: CreateSubscriptionRequest): Promise<Subscription> => {
    const response = await subscriptionApiService.post<ApiResponse<Subscription>>(
      '/api/v1/subscriptions',
      data
    );
    return unwrapResponse(response);
  },

  /** Get the currently active subscription for a user. */
  getActiveSubscription: async (userId: string): Promise<Subscription> => {
    const response = await subscriptionApiService.get<ApiResponse<Subscription>>(
      `/api/v1/subscriptions/user/${userId}/active`
    );
    return unwrapResponse(response);
  },

  /** Get all subscriptions for a user (history). */
  getUserSubscriptions: async (userId: string): Promise<Subscription[]> => {
    const response = await subscriptionApiService.get<ApiResponse<Subscription[]>>(
      `/api/v1/subscriptions/user/${userId}`
    );
    return unwrapResponse(response);
  },

  /** Change the plan on an existing subscription. */
  changePlan: async (subscriptionId: string, data: ChangePlanRequest): Promise<Subscription> => {
    const response = await subscriptionApiService.put<ApiResponse<Subscription>>(
      `/api/v1/subscriptions/${subscriptionId}/plan`,
      data
    );
    return unwrapResponse(response);
  },

  /** Cancel a subscription (with optional reason/feedback). */
  cancelSubscription: async (
    subscriptionId: string,
    data: CancelSubscriptionRequest
  ): Promise<void> => {
    // DELETE with body — use request() directly (same pattern as affiliator terminate)
    const response = await subscriptionApiService.request<ApiResponse<void>>(
      `/api/v1/subscriptions/${subscriptionId}`,
      {
        method: 'DELETE',
        body: JSON.stringify(data),
      }
    );
    unwrapResponse(response);
  },

  /** Reactivate a cancelled/suspended subscription. */
  reactivateSubscription: async (subscriptionId: string): Promise<Subscription> => {
    const response = await subscriptionApiService.post<ApiResponse<Subscription>>(
      `/api/v1/subscriptions/${subscriptionId}/reactivate`
    );
    return unwrapResponse(response);
  },

  /** Get subscription status for a user. */
  getSubscriptionStatus: async (userId: string): Promise<SubscriptionStatusResponse> => {
    const response = await subscriptionApiService.get<ApiResponse<SubscriptionStatusResponse>>(
      `/api/v1/subscriptions/user/${userId}/status`
    );
    return unwrapResponse(response);
  },

  /** Pause an active subscription. */
  pauseSubscription: async (subscriptionId: string): Promise<Subscription> => {
    const response = await subscriptionApiService.post<ApiResponse<Subscription>>(
      `/api/v1/subscriptions/${subscriptionId}/pause`
    );
    return unwrapResponse(response);
  },

  /** Resume a paused subscription. */
  resumeSubscription: async (subscriptionId: string): Promise<Subscription> => {
    const response = await subscriptionApiService.post<ApiResponse<Subscription>>(
      `/api/v1/subscriptions/${subscriptionId}/resume`
    );
    return unwrapResponse(response);
  },

  // --------------------------------------------------------------------------
  // Invoices
  // --------------------------------------------------------------------------

  /** Get paginated invoices for a user. */
  getUserInvoices: async (
    userId: string,
    page = 0,
    size = 10
  ): Promise<PaginatedResponse<Invoice>> => {
    const response = await subscriptionApiService.get<
      ApiResponse<PaginatedResponse<Invoice>> | PaginatedResponse<Invoice>
    >(`/api/v1/invoices/user/${userId}?page=${page}&size=${size}`);

    // Handle both wrapped and unwrapped paginated responses
    if (response && typeof response === 'object' && 'content' in response) {
      return response as PaginatedResponse<Invoice>;
    }
    const wrapped = response as ApiResponse<PaginatedResponse<Invoice>>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || 'Failed to fetch invoices');
    }
    return wrapped.data;
  },

  /** Get a single invoice by ID. */
  getInvoice: async (invoiceId: string): Promise<Invoice> => {
    const response = await subscriptionApiService.get<ApiResponse<Invoice>>(
      `/api/v1/invoices/${invoiceId}`
    );
    return unwrapResponse(response);
  },

  /** Download invoice PDF as a Blob. */
  getInvoicePdf: async (invoiceId: string): Promise<Blob> => {
    // Fetch the PDF directly — the ApiService will return the raw response text,
    // so we build the URL manually and fetch outside the service wrapper.
    const baseUrl = SUBSCRIPTION_API_BASE_URL.replace(/\/$/, '');
    const url = `${baseUrl}/api/v1/invoices/${invoiceId}/pdf`;
    const token = typeof window !== 'undefined' ? localStorage.getItem('five_ai_access_token') : null;
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Failed to download invoice PDF (${response.status})`);
    }
    return response.blob();
  },

  // --------------------------------------------------------------------------
  // Payments
  // --------------------------------------------------------------------------

  /** Process a payment for a subscription. */
  processPayment: async (data: ProcessPaymentRequest): Promise<PaymentTransaction> => {
    const response = await paymentApiService.post<ApiResponse<PaymentTransaction>>(
      '/api/v1/payments/process',
      data
    );
    return unwrapResponse(response);
  },

  /** Get available payment methods (all). */
  getPaymentMethods: async (): Promise<AvailablePaymentMethod[]> => {
    const response = await paymentApiService.get<ApiResponse<AvailablePaymentMethod[]>>(
      '/api/v1/payments/methods'
    );
    return unwrapResponse(response);
  },

  /** Get payment methods filtered by currency. */
  getPaymentMethodsByCurrency: async (currency: string): Promise<AvailablePaymentMethod[]> => {
    const response = await paymentApiService.get<ApiResponse<AvailablePaymentMethod[]>>(
      `/api/v1/payments/methods/by-currency?currency=${encodeURIComponent(currency)}`
    );
    return unwrapResponse(response);
  },

  /** Get payment transaction history for a user. */
  getUserTransactions: async (
    userId: string,
    page = 0,
    size = 10
  ): Promise<PaginatedResponse<PaymentTransaction>> => {
    const response = await paymentApiService.get<
      ApiResponse<PaginatedResponse<PaymentTransaction>> | PaginatedResponse<PaymentTransaction>
    >(`/api/v1/payments/user/${userId}/transactions?page=${page}&size=${size}`);

    if (response && typeof response === 'object' && 'content' in response) {
      return response as PaginatedResponse<PaymentTransaction>;
    }
    const wrapped = response as ApiResponse<PaginatedResponse<PaymentTransaction>>;
    if (!wrapped.success || !wrapped.data) {
      throw new Error(wrapped.message || 'Failed to fetch transactions');
    }
    return wrapped.data;
  },

  // --------------------------------------------------------------------------
  // Coupons
  // --------------------------------------------------------------------------

  /** Get usage summary for a user (tokens, sessions, etc.). */
  getUserUsageSummary: async (userId: string) => {
    const response = await subscriptionApiService.get<ApiResponse<Record<string, unknown>>>(
      `/api/v1/usage-metrics/users/${userId}/summary`
    );
    return unwrapResponse(response);
  },

  /** Validate a coupon code against a plan. */
  validateCoupon: async (code: string, planCode: string): Promise<CouponValidationResult> => {
    const response = await subscriptionApiService.post<ApiResponse<CouponValidationResult>>(
      '/api/v1/coupons/validate',
      { code, planCode }
    );
    return unwrapResponse(response);
  },
};
